import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  buildReturnToFromLocation,
  navigateWithReturn
} from '../../../../shared/navigation';
import Table from '../../../../shared/components/layout/Table';
import searchIcon from '../../../../shared/assets/svg/search.svg';
import filterIcon from '../../../../shared/assets/svg/filter.svg';
import reverseIcon from '../../../../shared/assets/svg/reverse.svg';
import { transformData } from '../../../utils/transformData';
import { useQuery } from '@tanstack/react-query';
import { getAllInvoices } from '../../../services/api/implementation/impl';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const InvoiceManagement = ({
  newAddedInvoice,
  newEditedInvoice,
  onEdit,
  onChangeActiveInvoiceId
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [reverseOrder, setReverseOrder] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const userId = Cookies.get('userId');

  // Track page view
  useEffect(() => {
    mixpanel.track('Super Admin Invoice Management Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { data: invoices, refetch: refetchInvoices } = useQuery({
    queryKey: ['invoices'],
    queryFn: getAllInvoices,
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Invoice Management Data Fetch Failed', {
          user_id: userId,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        toast.error(
          result?.response?.data?.message || result?.message || 'Failed to fetch invoices'
        );
        return;
      }

      mixpanel.track('Super Admin Invoice Management Data Fetch Success', {
        user_id: userId,
        invoices_count: result?.length || 0,
        invoices_with_due_amount: result?.filter((inv) => inv.due_amount > 0)?.length || 0,
        total_invoice_value: result?.reduce((sum, inv) => sum + (inv.amount || 0), 0) || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Invoice Management Data Fetch Failed', {
        user_id: userId,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
      toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch invoices');
    }
  });

  useEffect(() => {
    refetchInvoices();
    // toast.success(`Successfully fetched ${invoices?.length} invoices`);
  }, [invoices?.length, newAddedInvoice, refetchInvoices, newEditedInvoice]);

  // Track when new invoice is added or edited
  useEffect(() => {
    if (newAddedInvoice) {
      mixpanel.track('Super Admin Invoice Management New Invoice Added', {
        user_id: userId,
        timestamp: new Date().toISOString()
      });
    }
  }, [newAddedInvoice, userId]);

  useEffect(() => {
    if (newEditedInvoice) {
      mixpanel.track('Super Admin Invoice Management Invoice Edited', {
        user_id: userId,
        timestamp: new Date().toISOString()
      });
    }
  }, [newEditedInvoice, userId]);

  const transformedData = invoices?.map((el) =>
    transformData(
      {
        id: el.id,
        builder_name: el.builder?.name,
        project_name: el.project?.name,
        invoice_number: el.invoice_number,
        amount: el.amount,
        amount_paid: el.paid_amount ?? el.amount - el.due_amount,
        due_amount: el.due_amount,
        status: el.status
      },
      [
        'id',
        'builder_name',
        'project_name',
        'invoice_number',
        'amount',
        'amount_paid',
        'due_amount',
        'status',
        'actions'
      ]
    )
  );

  const filteredData = transformedData?.filter((row) =>
    row.builder_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (reverseOrder) {
    filteredData.reverse();
  }

  const handleSearchChange = (e) => {
    const query = e.target.value;

    mixpanel.track('Super Admin Invoice Management Search', {
      user_id: userId,
      search_query: query,
      results_count:
        transformedData?.filter((row) =>
          row.builder_name?.toLowerCase().includes(query.toLowerCase())
        )?.length || 0,
      timestamp: new Date().toISOString()
    });

    setSearchQuery(query);
  };

  const handleReverseOrderChange = () => {
    mixpanel.track('Super Admin Invoice Management Order Reversed', {
      user_id: userId,
      new_order: !reverseOrder ? 'reversed' : 'normal',
      timestamp: new Date().toISOString()
    });

    setReverseOrder(!reverseOrder);
  };

  const handleEditInvoice = (id) => {
    mixpanel.track('Super Admin Invoice Management Edit Clicked', {
      user_id: userId,
      invoice_id: id,
      timestamp: new Date().toISOString()
    });

    onEdit();
  };

  const handleInvoiceFilter = () => {
    mixpanel.track('Super Admin Invoice Management Filter Clicked', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });

    console.log('Opening invoice filter modal');
  };

  const handleChangeActiveInvoice = (id) => {
    const invoice = invoices?.find((inv) => inv.id === parseInt(id));

    mixpanel.track('Super Admin Invoice Management Invoice Selected', {
      user_id: userId,
      invoice_id: id,
      invoice_amount: invoice?.amount,
      due_amount: invoice?.due_amount,
      status: invoice?.status,
      timestamp: new Date().toISOString()
    });

    onChangeActiveInvoiceId(id);
  };

  const handleViewInvoiceDetails = (id) => {
    mixpanel.track('Super Admin Invoice Details Viewed', {
      user_id: userId,
      invoice_id: id,
      timestamp: new Date().toISOString()
    });
    navigateWithReturn(navigate, `/super-admin/billings-payment/invoices/${id}`, {
      returnTo: buildReturnToFromLocation(location)
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between px-6 py-4 bg-white  w-full shadow">
        <div className="flex gap-2">
          <div className="flex items-center border border-gray-500 px-2 rounded-md">
            <input
              type="text"
              placeholder="Search here..."
              className="w-[386px] p-1 outline-none"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <img src={searchIcon} alt="" />
          </div>
          {/* <img
            src={filterIcon}
            className="cursor-pointer"
            onClick={handleInvoiceFilter}
            alt=""
          />
          <img
            src={reverseIcon}
            alt="Reverse Order"
            onClick={handleReverseOrderChange}
            className="w-10 cursor-pointer"
          /> */}
        </div>
      </div>
      <div className="overflow-y-auto pt-3">
        <div className="p-6 h-[28rem]">
          <Table
            headers={[
              'Builder',
              'Project',
              'Invoice no.',
              'Invoice value',
              'Amount paid',
              'Amount due',
              'Status',
              'Actions'
            ]}
            data={filteredData}
            handleRowClick={handleViewInvoiceDetails}
            onEdit={(id) => handleChangeActiveInvoice(id)}
            identifier={'id'}
          />
        </div>
      </div>
    </div>
  );
};

export default InvoiceManagement;
